import { openModal } from "~/common/utils/ModalService";
import { type AddToCartHookProps, type AddToCartSubmitData } from "~/features/addToCart/hooks/useAddToCartForm";
import { type AddToCartSubmitHandler } from "~/features/addToCart/components/AddToCartProcess/AddToCartWizardShared";
import {
  createAddToCartCloseIntentAdapter,
  createEditPartCloseIntentAdapter,
} from "~/features/addToCart/AddToCartServiceCompat";
import { AddToCartWizard } from "~/features/addToCart/components/AddToCartProcess/AddToCartWizard";
import { EditPartWizard } from "~/features/addToCart/components/AddToCartProcess/EditPartWizard";
import { Permission, userHasAllPermissions } from "~/domains/user/utils/permissions";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";
import type { AddToCartSource } from "~/app/analytics/types";
import type { PurchasePlace, PurchaseType } from "~/app/analytics/ga/recommendations/types";
import { addToCart } from "~/features/addToCart/redux/addToCart/productAddFlow";
import { addToCartItemFromSubmitData } from "~/features/addToCart/redux/addToCart/addToCartItemFactory";
import type { AppDispatch } from "~/app/store";

async function openAddToCartWizard(
  hookProps: Omit<AddToCartHookProps, "editMode" | "priceReferenceSelection"> & {
    restrictionQuery: string;
    source?: AddToCartSource;
    showSuccessDrawer?: boolean;
    position?: number;
    onSuccessCallback?: () => void;
    onCancelCallback?: () => void;
  },
  context: {
    page: PurchasePlace;
    recommendationType?: PurchaseType;
    recommendationPlace?: PurchasePlace;
  },
  dispatch: AppDispatch,
) {
  const addToCartSource: AddToCartSource = hookProps.source ?? "unknown";
  const onSubmit = (submitData: AddToCartSubmitData) => {
    const productCode = submitData.derived.descriptor.base.code;
    return dispatch(
      addToCart({
        items: [
          addToCartItemFromSubmitData({
            submitData,
            productCode,
            restrictionQuery: hookProps.restrictionQuery,
            context,
            position: hookProps.position,
          }),
        ],
        page: context.page,
        source: addToCartSource,
        successUi: hookProps.showSuccessDrawer === false ? "none" : undefined,
        recommendationPlace: context.recommendationPlace,
        recommendationType: context.recommendationType,
      }),
    )
      .then(() => {
        hookProps.onSuccessCallback?.();
        return true;
      })
      .catch(() => false);
  };

  if (userHasAllPermissions(Permission.AVAILABILITY, Permission.PRICE)) {
    return openModal("add-to-cart-modal", (close) => (
      <AddToCartWizard
        onSubmit={onSubmit}
        editMode={false}
        onRequestClose={createAddToCartCloseIntentAdapter(close, hookProps.onCancelCallback)}
        {...hookProps}
      />
    ));
  }

  // Non customer flow. Here we don't know availabilities or prices, so we just perform an add to cart call with all possible data faked

  return dispatch(
    addToCart({
      items: [
        {
          productCode: hookProps.productCode,
          packageCode: INVALID_PACKAGE_CODE,
          quantity: hookProps.initialData?.quantity ?? 1,
          selectedDeliveryDate: null,
          selectedDeliveryDateIsManual: false,
          priceReference: hookProps.initialData?.priceReference ?? "",
          restrictedPackage: false,
        },
      ],
      page: context.page,
      source: addToCartSource,
      recommendationPlace: context.recommendationPlace,
      recommendationType: context.recommendationType,
    }),
  )
    .then(() => true)
    .catch(() => false);
}

async function openEditPartWizard(
  hookProps: Omit<AddToCartHookProps, "editMode" | "priceReferenceSelection"> & {
    restrictionQuery: string;
    onSubmit: AddToCartSubmitHandler;
  },
) {
  return openModal("edit-cart-modal", (close) => {
    return <EditPartWizard editMode={true} onRequestClose={createEditPartCloseIntentAdapter(close)} {...hookProps} />;
  });
}

export const AddToCartService = {
  openAddToCartWizard,
  openEditPartWizard,
};
